<template>
  <div class="main">
    <Sidebar
      :current-keywords="keywords"
      :current-filters="currentFilters"
      :filter-types="filterTypes"
      @search="setKeywords"
      @add-filter="addFilter"
      @remove-filter="removeFilter"
      @remove-all-filters="removeAllFilters"
    >
      <div slot="header" class="sidebar-header">
        <div class="title-bar row no-gutters px-0 py-3 d-xl-none px-lg-3">
          <div class="col-12 text-center mb-3 col-lg-4 text-lg-left mb-lg-0">
            <h1>Classroom Course Catalog</h1>
          </div>
          <div class="col-12 col-lg-8">
            <Toggles :toggles="toggles" />
          </div>
        </div>
        <div v-if="$root.isLoggedIn" class="d-xl-none px-3 bg-white">
          <FileFolderTabs :tabs="courseTypeTabs" current="classroom" />
        </div>
      </div>
      <div slot="custom-filters" class="custom-filters">
        <div class="inner pt-3">
          <hr class="mt-0" />
          <CustomFilters v-model="hasUpcomingEvents" />
          <hr v-if="tags.length > 0" />
          <Field
            v-for="(tag, i) in tags"
            :key="i"
            type="checkbox"
            name="tags[]"
            :id="`cb_tag_${tag.value}`"
            :label="tag.label"
            v-model="selectedTags"
            :checkbox-value="tag.value"
            class="custom-control custom-checkbox mb-1"
          />
        </div>
      </div>
    </Sidebar>

    <div class="title-bar row no-gutters p-3 d-none d-xl-flex">
      <div class="col-6">
        <h1>Classroom Course Catalog</h1>
      </div>
      <div class="col-6">
        <Toggles :toggles="toggles" />
      </div>
    </div>

    <div v-if="$root.isLoggedIn" class="d-none d-xl-flex px-3 bg-white">
      <FileFolderTabs :tabs="courseTypeTabs" current="classroom" />
    </div>

    <div class="content p-3">
      <div class="row">
        <div class="col-12">
          <transition name="fade">
            <Alert v-if="hasAlert" :alert="alert" />
          </transition>

          <div class="row d-none d-xl-flex">
            <div class="col-12 col-xl-4 mb-3 mb-xl-0 text-center text-xl-left">
              <a
                v-if="items.length > 0 && $root.isLoggedIn"
                href="#"
                @click.prevent="showExportOptions()"
                class="btn btn-info mb-3 mr-sm-1 mb-sm-0 mb-xl-3"
              >
                <span class="icon icon-download"></span>
                <span class="text">Export List</span>
              </a>
              <a
                v-if="showCourseRequestButton"
                href="#"
                @click.prevent="showCourseRequestNotice()"
                class="btn btn-info mb-xl-3"
              >
                <span class="icon icon-courses"></span>
                <span class="text">Request a Course</span>
              </a>
            </div>
            <div class="col-12 col-xl-4 mb-3 mb-xl-0 text-center">
              <Pagination
                class="d-inline-block"
                :total="totalPages"
                :value="parseInt(pagination.current)"
                @input="setCurrentPage"
              />
            </div>
            <div class="col-12 col-xl-4 text-center text-xl-right">
              <PageSize
                :value="parseInt(pagination.per_page)"
                @input="setPerPage"
              />
            </div>
          </div>

          <div class="row">
            <div
              :class="{
                'col-12': !showDetailsPane,
                'col-8 details-pane-open': showDetailsPane,
              }"
            >
              <ListTable
                :items="items"
                :columns="columns"
                :activeColumns="activeColumns"
                :hideNoResults="hideNoResults"
                :hasDetailsPane="showDetailsPane"
                @sort-by="setSort"
                @swap-column="setActiveColumn"
                @item-clicked="setSelectedItem"
              >
                <template v-slot:col_name="{ item }">
                  {{ item.name }}
                  <span v-if="item.is_featured" class="featured">Featured</span>
                </template>
                <template v-slot:col_cost="{ item }">{{
                  item.enrollment_cost | cost
                }}</template>
                <template v-slot:list-card="{ item, id }">
                  <ListCard :item="item" :id="id" :eventBus="listEvents" />
                </template>
                <template v-slot:list-menu="{ item, id }">
                  <ListMenu :item="item" :id="id" :eventBus="listEvents" />
                </template>
              </ListTable>
            </div>
            <div v-if="showDetailsPane" class="col-4 details-pane-wrap mb-3">
              <DetailsPane
                :course="selectedItem"
                :tab="selectedTab"
                @close="setSelectedItem(null)"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-12 col-xl-4 mb-3 mb-xl-0 text-center text-xl-left">
              <a
                v-if="items.length > 0 && $root.isLoggedIn"
                href="#"
                @click.prevent="showExportOptions()"
                class="btn btn-info mb-3 mr-sm-1 mb-sm-0 mb-xl-3"
              >
                <span class="icon icon-download"></span>
                <span class="text">Export List</span>
              </a>
              <a
                v-if="showCourseRequestButton"
                href="#"
                @click.prevent="showCourseRequestNotice()"
                class="btn btn-info mb-xl-3"
              >
                <span class="icon icon-courses"></span>
                <span class="text">Request a Course</span>
              </a>
            </div>
            <div class="col-12 col-xl-4 mb-3 mb-xl-0 text-center">
              <Pagination
                class="d-inline-block"
                :total="totalPages"
                :value="parseInt(pagination.current)"
                @input="setCurrentPage"
              />
            </div>
            <div class="col-12 col-xl-4 text-center text-xl-right">
              <PageSize
                :value="parseInt(pagination.per_page)"
                @input="setPerPage"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
import ListingMixin from "mixins/Listing";
import ListCard from "components/partials/Courses/ListCard";
import ListMenu from "components/partials/Courses/ListMenu";
import DetailsPane from "components/partials/Courses/DetailsPane";
import CustomFilters from "./partials/CourseCatalogFilters";
import CourseRequestNotice from "../partials/CourseRequestNotice";

export default {
  mixins: [DisplaysAlerts, ListingMixin],
  components: {
    ListCard,
    ListMenu,
    DetailsPane,
    CustomFilters,
    CourseRequestNotice,
  },
  props: {
    tags: { type: Array, default: () => [] },
    toggles: { type: Array, default: () => [] },
  },
  data() {
    return {
      hasUpcomingEvents: false,
      selectedTags: [],
      customFilters: ["has_upcoming_events", "tag"],
    };
  },
  computed: {
    courseTypeTabs() {
      return [
        {
          label: "Online",
          icon: "online",
          url: this.route("catalog.online"),
        },
        {
          label: "Classroom",
          icon: "classroom",
          url: this.route("catalog.classroom"),
        },
      ];
    },
    showCourseRequestButton() {
      return (
        this.items.length > 0 && (this.$root.isManager || this.$root.isAdmin)
      );
    },
  },
  watch: {
    hasUpcomingEvents(val) {
      if (!!val) {
        this.addFilter("has_upcoming_events", true);
      } else {
        this.clearFilter("has_upcoming_events");
      }
    },
    selectedTags(val) {
      this.replaceFilter("tag", val);
    },
  },
  methods: {
    showCourseRequestNotice() {
      this.$modal(CourseRequestNotice)
        .then(() => {})
        .catch(() => {});
    },
    handleMenuAction(action, item) {
      //
    },
  },
  mounted() {
    if (_.has(this.filters, "has_upcoming_events")) {
      this.hasUpcomingEvents = true;
    }
    if (_.has(this.filters, "tag")) {
      this.selectedTags = this.filters.tag || [];
    }
    this.listEvents.$on("menu-action", this.handleMenuAction);
    this.listEvents.$on("show-details-pane", this.setSelectedItem);
    this.$root.eventBus.$on('clear-all-filters', () => this.selectedTags = [])
  },
};
</script>